import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleAiListing: React.FC = () => {
  const BASE = '/images/help/ai-listing';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/ail-step-1.png`, alt: 'AI listing generation overview', caption: 'Step 1 – How Coderom AI rewrites your product listing' },
    ],
    step2: [
      { src: `${BASE}/ail-step-2.png`, alt: 'Adding a product with AI optimisation enabled', caption: 'Step 2 – Import a product with AI optimisation turned on' },
    ],
    step3: [
      { src: `${BASE}/ail-step-3.png`, alt: 'Reviewing and editing an AI-generated draft', caption: 'Step 3 – Review your AI draft and make edits before publishing' },
    ],
    step4: [
      { src: `${BASE}/ail-step-4.png`, alt: 'AI title optimisation interface', caption: 'Step 4 – Run AI title optimisation on existing listings' },
    ],
    step5: [
      { src: `${BASE}/ail-step-5.png`, alt: 'AI settings configuration panel', caption: 'Step 5 – Customise AI behaviour in Settings' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="AI-Powered Product Listing | CodeRom Help"
        description="How CodeRom's AI creates optimised eBay listings from Amazon products automatically."
      />
      <HelpBreadcrumbSchema slug="ai-listing" name="AI-powered product listing" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">AI-powered product listing</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>AI-powered product listing</h1>
                <p className="article-subtitle">
                  Learn how Coderom's AI creates optimised eBay listings from Amazon products —
                  better titles, descriptions, and images that help your products sell faster.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 8 min read</span>
                  <span className="category">Product Listing</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. How AI listing works</a></li>
                  <li><a href="#step2">2. Add a product with AI optimisation</a></li>
                  <li><a href="#step3">3. Review and edit your AI draft</a></li>
                  <li><a href="#step4">4. AI title optimisation</a></li>
                  <li><a href="#step5">5. Customise AI settings</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: How AI listing works</h2>
                  <p>
                    When you add an Amazon product to Coderom, the AI analyses the original listing
                    and creates an optimised version for eBay. This includes:
                  </p>
                  <ul>
                    <li>A rewritten title that follows eBay best practices (80 characters, keyword-rich)</li>
                    <li>A clean, professional product description</li>
                    <li>Properly formatted item specifics</li>
                    <li>Optimised images from the Amazon listing</li>
                  </ul>
                  <p>
                    The AI removes Amazon-specific references and branding, making your listing look
                    professional and original.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: How AI listing works" />
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Add a product with AI optimisation</h2>
                  <ol>
                    <li>Go to <strong>Add Product</strong> from the sidebar</li>
                    <li>Paste the Amazon ASIN or product URL</li>
                    <li>Click <strong>Search</strong> — Coderom will fetch the product details</li>
                    <li>The AI will automatically generate an optimised listing</li>
                    <li>The product appears in your <strong>Draft Products</strong> with an "AI Optimised" badge</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Add a product with AI optimisation" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> You can also enable AI optimisation for bulk imports.
                      Go to <strong>Bulk Import</strong> and make sure "AI Optimise" is toggled on
                      before importing your CSV file.
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Review and edit your AI draft</h2>
                  <ol>
                    <li>Go to <strong>Draft Products</strong></li>
                    <li>Click on any product to open the editor</li>
                    <li>Review the AI-generated title, description, and item specifics</li>
                    <li>Make any changes you'd like — the AI draft is fully editable</li>
                    <li>Click <strong>Save</strong> when you're happy with the listing</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Review and edit your AI draft" />
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: AI title optimisation</h2>
                  <p>
                    Coderom's AI can also optimise titles for your existing listings:
                  </p>
                  <ol>
                    <li>Go to <strong>Draft Products</strong> or <strong>Active Products</strong></li>
                    <li>Select one or more products</li>
                    <li>Click <strong>AI Optimise Titles</strong></li>
                    <li>The AI will rewrite each title following eBay SEO best practices</li>
                    <li>Review the new titles and click <strong>Apply</strong></li>
                  </ol>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: AI title optimisation" />

                  <div className="comparison-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Before</th>
                          <th>After</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Amazon Fire TV Stick 4K Max streaming device, supports Wi-Fi 6E, Alexa Voice Remote</td>
                          <td>Fire TV Stick 4K Max Streaming Device Wi-Fi 6E Alexa Voice Remote Smart TV</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> AI title optimisation works in batches — you can optimise
                      up to 50 titles at once. The process takes about 2-3 seconds per title.
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Customise AI settings</h2>
                  <p>
                    You can control how the AI generates listings. Go to <strong>Settings → AI Listing</strong>:
                  </p>

                  <div className="settings-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>AI optimisation</strong></td>
                          <td>Enable/disable AI for new products</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Title style</strong></td>
                          <td>Choose between keyword-focused or readable titles</td>
                          <td>Keyword-focused</td>
                        </tr>
                        <tr>
                          <td><strong>Description length</strong></td>
                          <td>Short, medium, or detailed descriptions</td>
                          <td>Medium</td>
                        </tr>
                        <tr>
                          <td><strong>Remove Amazon refs</strong></td>
                          <td>Strip Amazon branding from descriptions</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Auto item specifics</strong></td>
                          <td>AI fills in missing item specifics</td>
                          <td>On</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step5} stepLabel="Step 5: Customise AI settings" />
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that you understand AI listing, explore these related features:</p>
                  <div className="next-steps">
                    <Link to="/help/bulk-import" className="next-step-card">
                      <div className="next-step-icon">📦</div>
                      <div className="next-step-content">
                        <h4>Bulk product import</h4>
                        <p>Import hundreds of products at once with AI optimisation enabled</p>
                      </div>
                    </Link>
                    <Link to="/help/templates" className="next-step-card">
                      <div className="next-step-icon">🎨</div>
                      <div className="next-step-content">
                        <h4>Professional listing templates</h4>
                        <p>Combine AI-generated content with custom templates for a branded look</p>
                      </div>
                    </Link>
                    <Link to="/help/pricing" className="next-step-card">
                      <div className="next-step-icon">💰</div>
                      <div className="next-step-content">
                        <h4>Smart repricing & pricing rules</h4>
                        <p>Automatically adjust prices based on Amazon changes and market conditions</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .settings-table,
        .comparison-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .settings-table table,
        .comparison-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .settings-table th,
        .comparison-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .settings-table td,
        .comparison-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .settings-table tbody tr:last-child td,
        .comparison-table tbody tr:last-child td {
          border-bottom: none;
        }
        .settings-table tbody tr:hover,
        .comparison-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .settings-table,
          .comparison-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleAiListing;
